import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import DataGrid from '../../../components/DataGrid';
import Forms from '../../../components/Forms';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import {useModal} from '../../../contexts/ModalContext';
import {ReactComponent as DeleteIcon} from '../../../assets/images/delete.svg';
import {ReactComponent as EditIcon} from '../../../assets/images/edit.svg';
import DiscographyAlbumModal from './DiscographyAlbumModal';

const DiscographyAlbums = () => {
    const {t} = useTranslation();
    const {openModal, confirm} = useModal();

    const [albums, setAlbums] = useState([]);
    const [q, setQ] = useState('');
    const [typeFilter, setTypeFilter] = useState(null);

    const fetchAlbums = useCallback(async () => {
        try {
            const res = await API.get(API_ENDPOINTS.discographyAlbums.list);
            setAlbums(res.data || []);
        } catch (e) {
            console.error('Failed to fetch discography albums:', e);
        }
    }, []);

    useEffect(() => {
        fetchAlbums();
    }, [fetchAlbums]);

    const openAlbumModal = (album) => {
        openModal(
            DiscographyAlbumModal,
            {
                album,
                onAlbumUpdate: fetchAlbums
            },
            {title: album ? t('discography.editAlbum') : t('discography.addAlbum')}
        );
    };

    const confirmDelete = async (album) => {
        const ok = await confirm(t('discography.confirmDeletion', {name: album.name}), {});
        if (ok === true) {
            try {
                await API.delete(API_ENDPOINTS.discographyAlbums.delete(album._id));
                setAlbums(prev => prev.filter(a => a._id !== album._id));
            } catch (e) {
                console.error('Failed to delete album:', e);
            }
        }
    };

    const rows = useMemo(() => {
        const qlc = q.trim().toLowerCase();
        return (albums || []).filter(a => {
            if (typeFilter && a.type !== typeFilter.value) return false;
            if (qlc && !(a.name || '').toLowerCase().includes(qlc)) return false;
            return true;
        });
    }, [albums, q, typeFilter]);

    const columns = [
        {
            key: 'previewImg',
            name: t('discography.cover'),
            width: 90,
            renderCell: ({row}) => row.previewImg
                ? <img
                    alt={row.name}
                    src={row.previewImg}
                    className="h-12 w-12 object-cover rounded"
                />
                : <div className="h-12 w-12 bg-gray-100 rounded"/>
        },
        {
            key: 'name',
            name: t('discography.name'),
            renderCell: ({row}) => (
                <label className="flex justify-between">
                    {row.name}
                    <div className="flex">
                        <DeleteIcon className="w-4 cursor-pointer fill-red-700 m-2" onClick={() => confirmDelete(row)}/>
                        <EditIcon className="w-4 cursor-pointer text-blue-800 h-full"
                                  onClick={() => openAlbumModal(row)}/>
                    </div>
                </label>
            )
        },
        {
            key: 'type',
            name: t('discography.type'),
            renderCell: ({row}) => t(row.type)
        },
        {
            key: 'description',
            name: t('discography.description'),
            renderCell: ({row}) => row.description
        },
        {
            key: 'purchaseUrl',
            name: t('discography.purchaseUrl'),
            renderCell: ({row}) => row.purchaseUrl
                ? <a className="text-blue-700 underline" href={row.purchaseUrl} target="_blank"
                     rel="noreferrer">{row.purchaseUrl}</a>
                : '-'
        },
        {
            key: 'year',
            name: t('discography.year')
        },
        {
            key: 'createdAt',
            name: t('createdAt'),
            renderCell: ({row}) => row.createdAt ? moment(row.createdAt).format('DD.MM.YYYY HH:mm') : '-'
        },
        {
            key: 'updatedAt',
            name: t('updatedAt'),
            renderCell: ({row}) => row.updatedAt ? moment(row.updatedAt).format('DD.MM.YYYY HH:mm') : '-'
        }
    ].map(c => ({...c, resizable: true}));

    const typeOptions = [
        {value: 'lp', label: t('discography.lp')},
        {value: 'mc', label: t('discography.mc')},
        {value: 'cd', label: t('discography.cd')},
        {value: 'sp', label: t('discography.sp')}
    ];

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <Forms.Button label={t('discography.addAlbum')} onClick={() => openAlbumModal()}/>
                <div className="grid grid-cols-2 gap-3 items-end">
                    <Forms.MultiCheckbox
                        label={t('discography.type')}
                        value={typeFilter}
                        options={typeOptions}
                        onChange={setTypeFilter}
                        isClearable
                    />
                    <Forms.Text
                        label={t('search')}
                        placeholder={t('discography.searchByName')}
                        value={q}
                        onChange={setQ}
                    />
                </div>
            </div>

            {(q || typeFilter) && (
                <div className="flex justify-end mb-3">
                    <Forms.Button
                        variant="secondary"
                        label={t('clearFilters')}
                        onClick={() => {
                            setQ('');
                            setTypeFilter(null);
                        }}
                    />
                </div>
            )}

            <DataGrid rows={rows} columns={columns}/>
        </div>
    );
};

export default DiscographyAlbums;
